/**
 * WagryCalendar.js — month-grid view of queued + scheduled adverts.
 *
 *   • Shows one cell per SAST calendar day for the current month.
 *   • Each cell lists posts targeting that day with platform icon + title.
 *   • Drag-and-drop a post pill onto a different day → calls
 *     POST /api/adverts/{id}/queue/reschedule with the new date+time.
 *   • Per-platform colour coding (FB blue, IG pink, TikTok black).
 *   • Prev/Next month navigation; click a post pill to open Preview.
 *
 * Author: Die Biblioteek — 2026-06-23
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Facebook, Instagram, Music2, Youtube, Eye } from 'lucide-react';

const PLATFORM_META = {
  facebook: { icon: Facebook, bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300' },
  instagram: { icon: Instagram, bg: 'bg-pink-100', text: 'text-pink-800', border: 'border-pink-300' },
  tiktok: { icon: Music2, bg: 'bg-gray-200', text: 'text-gray-900', border: 'border-gray-400' },
  youtube: { icon: Youtube, bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' },
};

const AF_MONTHS = [
  'Januarie', 'Februarie', 'Maart', 'April', 'Mei', 'Junie',
  'Julie', 'Augustus', 'September', 'Oktober', 'November', 'Desember',
];
const AF_DAYS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Sa', 'So'];

// Build flat list of {advert, platform, dateISO, state} entries — one per scheduled platform
const explodeAdverts = (adverts) => {
  const items = [];
  (adverts || []).forEach(ad => {
    const qp = ad.queue_plan || {};
    if (Object.keys(qp).length > 0) {
      Object.entries(qp).forEach(([p, info]) => {
        const dateISO = info?.target_date || ad.scheduled_for;
        if (!dateISO) return;
        if (info?.state === 'cancelled') return;
        items.push({ advert: ad, platform: p, dateISO, state: info.state, queueDriven: true });
      });
    } else if (ad.scheduled_for) {
      (ad.platforms || []).forEach(p => {
        items.push({ advert: ad, platform: p, dateISO: ad.scheduled_for, state: 'scheduled', queueDriven: false });
      });
    }
  });
  return items;
};

const sastDateKey = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  // Format as YYYY-MM-DD in SAST (UTC+2)
  const sast = new Date(d.getTime() + 2 * 60 * 60 * 1000);
  return sast.toISOString().slice(0, 10);
};

const buildMonthGrid = (year, monthIndex) => {
  // monthIndex: 0-11
  const first = new Date(Date.UTC(year, monthIndex, 1));
  // Monday-first week → 0 = Mon ... 6 = Sun
  let firstDow = first.getUTCDay() - 1;
  if (firstDow < 0) firstDow = 6;
  const daysInMonth = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, key });
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
};

export default function WagryCalendar({ adverts, apiBase, onChange, onPreview }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [platformFilter, setPlatformFilter] = useState({ facebook: true, instagram: true, tiktok: true, youtube: true });
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const items = useMemo(() => explodeAdverts(adverts), [adverts]);
  const itemsByDay = useMemo(() => {
    const m = new Map();
    items.forEach(it => {
      if (!platformFilter[it.platform]) return;
      const k = sastDateKey(it.dateISO);
      if (!k) return;
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(it);
    });
    // sort each day by time
    m.forEach(arr => arr.sort((a, b) => new Date(a.dateISO) - new Date(b.dateISO)));
    return m;
  }, [items, platformFilter]);

  const grid = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const todayKey = sastDateKey(new Date().toISOString());

  const go = (dir) => {
    let m = viewMonth + dir;
    let y = viewYear;
    if (m < 0) { m = 11; y--; }
    if (m > 11) { m = 0; y++; }
    setViewMonth(m);
    setViewYear(y);
  };

  const handleDragStart = (e, item) => {
    setDragging(item);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragEnd = () => { setDragging(null); setDragOver(null); };
  const handleDragOver = (e, key) => { e.preventDefault(); setDragOver(key); };

  const handleDrop = async (e, dayKey) => {
    e.preventDefault();
    if (!dragging || !dayKey) { setDragOver(null); return; }
    const currentKey = sastDateKey(dragging.dateISO);
    if (currentKey === dayKey) { setDragOver(null); setDragging(null); return; }
    if (!dragging.queueDriven) {
      toast.warning('Plaas Later advertensies kan nie hier herskeduleer word nie. Verwyder en herskep asseblief.');
      setDragOver(null); setDragging(null); return;
    }
    // Preserve time-of-day from the source target_date. target_date is stored in UTC,
    // and the calendar formats events back to SAST for display — using getUTCHours()
    // here keeps the SAST display time stable across the drop (since both source and
    // destination are encoded as UTC at the same hour-of-day).
    const t = new Date(dragging.dateISO);
    const [Y, M, D] = dayKey.split('-').map(Number);
    const sastTarget = new Date(Date.UTC(Y, M - 1, D, t.getUTCHours(), t.getUTCMinutes()));
    if (sastTarget.getTime() <= Date.now()) {
      toast.error('Nuwe datum moet in die toekoms wees.');
      setDragOver(null); setDragging(null); return;
    }
    try {
      await axios.post(`${apiBase}/adverts/${dragging.advert.id}/queue/reschedule`, {
        scheduled_for: sastTarget.toISOString(),
        platforms: [dragging.platform],
      });
      toast.success(`Geskuif na ${D} ${AF_MONTHS[M - 1]}.`);
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kon nie herskeduleer nie.');
    } finally {
      setDragOver(null); setDragging(null);
    }
  };

  const togglePlatform = (p) => setPlatformFilter(prev => ({ ...prev, [p]: !prev[p] }));

  return (
    <div className="space-y-3" data-testid="wagry-calendar">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => go(-1)} data-testid="cal-prev-btn">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-md">
            <CalendarIcon className="h-4 w-4 text-amber-700" />
            <span className="font-semibold text-amber-900" data-testid="cal-month-label">
              {AF_MONTHS[viewMonth]} {viewYear}
            </span>
          </div>
          <Button size="sm" variant="outline" onClick={() => go(1)} data-testid="cal-next-btn">
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); }} data-testid="cal-today-btn">
            Vandag
          </Button>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">Filter:</span>
          {Object.keys(PLATFORM_META).map(p => {
            const meta = PLATFORM_META[p];
            const Icon = meta.icon;
            const active = platformFilter[p];
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePlatform(p)}
                className={`flex items-center gap-1 px-2 py-1 rounded border ${active ? `${meta.bg} ${meta.text} ${meta.border}` : 'bg-gray-100 text-gray-400 border-gray-200'}`}
                data-testid={`cal-filter-${p}`}
              >
                <Icon className="h-3 w-3" /> {p}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-gray-500 uppercase">
        {AF_DAYS.map(d => <div key={d} className="py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {grid.map((cell, idx) => {
          if (!cell) return <div key={`empty-${idx}`} className="min-h-[100px]" />;
          const events = itemsByDay.get(cell.key) || [];
          const isToday = cell.key === todayKey;
          const isPast = cell.key < todayKey;
          const dropActive = dragOver === cell.key && dragging;
          return (
            <div
              key={cell.key}
              onDragOver={(e) => handleDragOver(e, cell.key)}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => handleDrop(e, cell.key)}
              className={`min-h-[100px] border rounded-md p-1 text-left transition ${
                isToday ? 'border-amber-500 bg-amber-50/40' :
                isPast ? 'border-gray-200 bg-gray-50/40 text-gray-400' :
                'border-gray-200 bg-white'
              } ${dropActive ? 'ring-2 ring-amber-500 bg-amber-100/60' : ''}`}
              data-testid={`cal-day-${cell.key}`}
            >
              <div className="flex items-center justify-between text-xs font-semibold">
                <span className={isToday ? 'text-amber-700' : ''}>{cell.day}</span>
                {events.length > 0 && (
                  <span className="text-gray-400">{events.length}</span>
                )}
              </div>
              <div className="space-y-0.5 mt-1">
                {events.slice(0, 4).map((ev, i) => {
                  const meta = PLATFORM_META[ev.platform];
                  const Icon = meta?.icon;
                  const t = new Date(ev.dateISO).toLocaleTimeString('af-ZA', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Johannesburg' });
                  return (
                    <div
                      key={`${ev.advert.id}-${ev.platform}-${i}`}
                      draggable={ev.queueDriven}
                      onDragStart={(e) => handleDragStart(e, ev)}
                      onDragEnd={handleDragEnd}
                      className={`group flex items-center gap-1 text-[10px] px-1 py-0.5 rounded ${meta?.bg} ${meta?.text} truncate ${ev.queueDriven ? 'cursor-move' : 'cursor-pointer'}`}
                      title={`${t} · ${ev.advert.title} (${ev.platform}${ev.queueDriven ? '' : ' — Plaas Later'})`}
                      data-testid={`cal-event-${ev.advert.id}-${ev.platform}`}
                    >
                      {Icon && <Icon className="h-3 w-3 flex-shrink-0" />}
                      <span className="truncate flex-1">{t} {ev.advert.title}</span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onPreview && onPreview(ev.advert); }}
                        className="opacity-0 group-hover:opacity-100 transition"
                        title="Voorskou"
                      >
                        <Eye className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
                {events.length > 4 && (
                  <button
                    type="button"
                    onClick={() => onPreview && events[0] && onPreview(events[0].advert)}
                    className="text-[10px] text-gray-500 hover:underline w-full text-left"
                  >
                    +{events.length - 4} meer
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-500">
        Sleep ’n plasing na ’n ander dag om dit te herskeduleer. Tyd-van-dag bly behoue. Plaas Later items kan nie hier geskuif word nie.
      </p>
    </div>
  );
}
